import React, { FormEvent } from "react";
import { Mail, Send } from "lucide-react";
import { MultiSelectInputBox, TextInputBox } from "src/components/ui/inputs";
import { FormLvlErrorMessage } from "src/components/ui/feedback";
import { useScrollOnMessage } from "src/hooks";
import type { MultiSelectOption } from "src/components/ui/inputs/multiSelectInputBox";

export interface BulkEmailToVendorsFormData {
  vendorNames: string[];
  subject: string;
  body: string;
}

export type BulkEmailToVendorsFormDataKey = keyof BulkEmailToVendorsFormData;

interface BulkEmailToVendorsFormProps {
  formData: BulkEmailToVendorsFormData;
  vendorOptions: MultiSelectOption[];
  formLvlErrorMessage?: string;
  isFormComplete: boolean;
  handleInputChange: (
    field: BulkEmailToVendorsFormDataKey,
    value: BulkEmailToVendorsFormData[BulkEmailToVendorsFormDataKey]
  ) => void;
  handleSubmit: (e?: FormEvent) => Promise<void>;
  onClear: () => void;
}

const BulkEmailToVendorsForm: React.FC<BulkEmailToVendorsFormProps> = ({
  formData,
  vendorOptions,
  formLvlErrorMessage,
  isFormComplete,
  handleInputChange,
  handleSubmit,
  onClear
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="overflow-hidden rounded-2xl bg-white shadow-soft">
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold tracking-tight text-white">Bulk Email to Vendors</h1>
            <p className="text-primary-100 mt-2 text-lg">
              Send one email per vendor from your address. Each vendor receives a single email to all their addresses.
            </p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="mb-8">
              <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-6">
                <h2 className="mb-4 flex items-center text-xl font-semibold text-secondary-800">
                  <Mail className="mr-2 h-5 w-5 text-primary-600" />
                  Select Vendors
                </h2>
                <div className="space-y-2">
                  <label className="mb-2 block text-sm font-medium text-secondary-700">Vendors</label>
                  <MultiSelectInputBox
                    id="bulk-email-vendors"
                    label=""
                    values={formData.vendorNames}
                    options={vendorOptions}
                    onChange={(values: string[]) => handleInputChange("vendorNames", values)}
                    placeholder="Select vendors..."
                    showBadges={true}
                  />
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-soft">
                <div className="p-6">
                  <h2 className="mb-4 flex items-center text-xl font-semibold text-secondary-800">
                    <Send className="mr-2 h-5 w-5 text-primary-600" />
                    Compose
                  </h2>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="mb-2 block text-sm font-medium text-secondary-700">Subject</label>
                      <TextInputBox
                        id="bulk-email-subject"
                        placeHolder="Email subject"
                        inputValue={formData.subject}
                        onInputChange={(value: string) => handleInputChange("subject", value)}
                        maxLength={500}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="mb-2 block text-sm font-medium text-secondary-700">Body</label>
                      <TextInputBox
                        id="bulk-email-body"
                        placeHolder="Email body (plain text)"
                        inputValue={formData.body}
                        onInputChange={(value: string) => handleInputChange("body", value)}
                        multiline
                        rows={8}
                        maxLength={50000}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>

            <div className="mt-8 flex flex-col items-center justify-start gap-4 border-t border-secondary-200 pt-6 sm:flex-row">
              <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
                <button
                  className={`transform rounded-lg px-8 py-3 font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !isFormComplete
                      ? "cursor-not-allowed bg-secondary-400"
                      : "bg-gradient-to-r from-primary-600 to-primary-700 shadow-lg hover:scale-105 hover:from-primary-700 hover:to-primary-800 hover:shadow-xl focus:ring-primary-500"
                  }`}
                  disabled={!isFormComplete}
                  type="submit"
                >
                  <span className="flex items-center">
                    <Send className="mr-2 h-5 w-5" />
                    Send emails
                  </span>
                </button>

                <button
                  className="rounded-lg border-2 border-secondary-200 bg-white px-8 py-3 font-semibold text-secondary-700 transition-all duration-200 hover:scale-105 hover:bg-secondary-50 hover:border-secondary-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
                  onClick={onClear}
                  type="button"
                >
                  Clear
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { BulkEmailToVendorsForm };
